import { cloneElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '@shared/lib';
import { Label } from '../Label';

export interface FieldProps {
  /** Visible label text. */
  label: ReactNode;
  /** Exactly one form control (e.g. `<Input>`, `<Select>`). */
  children: ReactElement<Record<string, unknown>>;
  /** Helper text shown below the control when there's no error. */
  description?: ReactNode;
  /** Error message — shows in danger styling and sets `aria-invalid` on the control. */
  error?: ReactNode;
  /** Marks the field required (adds a "*" and `aria-required`). */
  required?: boolean;
  /** Trailing action aligned with the label (e.g. a "Forgot password?" link). */
  action?: ReactNode;
  /**
   * Always reserve one line of space below the control for the message, so toggling an
   * error on/off never shifts the field (or anything after it) up or down.
   */
  reserveSpace?: boolean;
  /** Override the generated id (otherwise `useId`). */
  id?: string;
  className?: string;
}

/**
 * Labelled form field. Wires `htmlFor`/`id`, `aria-describedby` (helper + error), and
 * `aria-invalid` onto its single child control so every field is accessible by default —
 * replaces the ad-hoc `<label> + <Input> + <p role="alert">` triple.
 */
export function Field({
  label,
  children,
  description,
  error,
  required = false,
  action,
  reserveSpace = false,
  id: idProp,
  className,
}: FieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const descId = description ? `${id}-description` : undefined;
  const errId = error ? `${id}-error` : undefined;
  const describedBy = [descId, errId].filter(Boolean).join(' ');

  const controlProps: Record<string, unknown> = { id };
  if (error) controlProps['aria-invalid'] = true;
  if (describedBy) controlProps['aria-describedby'] = describedBy;
  if (required) controlProps['aria-required'] = true;

  return (
    <div data-slot="field" className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id} className="font-display font-semibold text-fg">
          {label}
          {required ? <span className="text-danger"> *</span> : null}
        </Label>
        {action}
      </div>
      {cloneElement(children, controlProps)}
      {error || description || reserveSpace ? (
        <div className={cn(reserveSpace && 'min-h-4')}>
          {error ? (
            <p id={errId} role="alert" className="text-xs text-danger">
              {error}
            </p>
          ) : description ? (
            <p id={descId} className="text-xs text-muted">
              {description}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
